/**
 * AI provider settings service — wraps the backend /api/v1/ai API.
 *
 * Lets the operator pick which AI runs each capability (text / json /
 * vision / embeddings / transcription). Provider catalogue itself
 * comes from the backend so we don't hard-code the list in two places.
 */
import api from './api';

export interface ServiceResult<T> {
  data?: T;
  error?: string;
}

function extractError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    fallback
  );
}

export type AICapability =
  | 'text'
  | 'json'
  | 'vision'
  | 'embeddings'
  | 'transcription';

export interface AIProviderInfo {
  name: string;            // stable id: "anthropic" | "openai" | "grok"
  display_name: string;
  default_model: string;
  capabilities: AICapability[];
  configured: boolean;     // has an active API key
}

export interface AICapabilitySetting {
  capability: AICapability;
  provider_name: string;
  model: string | null;
  updated_at?: string;
}

export async function listProviders(): Promise<ServiceResult<AIProviderInfo[]>> {
  try {
    const { data } = await api.get<AIProviderInfo[]>('/ai/providers');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load AI providers') };
  }
}

export async function listSettings(): Promise<ServiceResult<AICapabilitySetting[]>> {
  try {
    const { data } = await api.get<AICapabilitySetting[]>('/ai/settings');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load AI settings') };
  }
}

export async function upsertSetting(
  capability: AICapability,
  providerName: string,
  model?: string | null,
): Promise<ServiceResult<AICapabilitySetting>> {
  try {
    const { data } = await api.put<AICapabilitySetting>(
      `/ai/settings/${capability}`,
      { provider_name: providerName, model: model ?? null },
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to save AI setting') };
  }
}

export async function clearSetting(
  capability: AICapability,
): Promise<ServiceResult<boolean>> {
  try {
    await api.delete(`/ai/settings/${capability}`);
    return { data: true };
  } catch (err) {
    return { error: extractError(err, 'Failed to clear AI setting') };
  }
}

// ---------------------------------------------------------------------------
// Multi-AI consensus
// ---------------------------------------------------------------------------

export interface ConsensusRequest {
  prompt: string;
  system?: string;
  providers?: string[];
  max_tokens?: number;
}

export interface ConsensusResult {
  prompt: string;
  responses: Record<string, string | null>;
  agreement: number | null;       // 0.0 - 1.0, null when <2 succeeded
  succeeded: string[];
  failed: string[];
  majority: string | null;
}

export async function runConsensus(
  body: ConsensusRequest,
): Promise<ServiceResult<ConsensusResult>> {
  try {
    const { data } = await api.post<ConsensusResult>('/ai/consensus', body);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Consensus call failed') };
  }
}

const aiSettingsService = {
  listProviders,
  listSettings,
  upsertSetting,
  clearSetting,
  runConsensus,
};

export default aiSettingsService;
